'use client';

import { useTranslations } from '@dealerportal/i18n/client';
import { useClientContext } from '@dealerportal/react-helpers';
import { BetterButton } from '@dealerportal/ui-components/BetterButton';
import classNames from 'classnames';
import type { FC } from 'react';

interface FilterToggleProps {
  filtersVisible: boolean;
  toggleFilters?: () => void;
  className?: string;
}

export const FilterToggle: FC<FilterToggleProps> = ({
  filtersVisible,
  toggleFilters,
  className,
}) => {
  const isMobileDevice = useClientContext().isMobileDevice;
  const t = useTranslations('common');

  return (
    <div className={classNames('ui-relative', className)}>
      <BetterButton
        btnStyle="neutral"
        attributes={{
          onClick: toggleFilters,
        }}
        replaceClass={{
          'ui-h-12': 'ui-h-10',
          '!ui-w-max': '!ui-w-full md:!ui-w-max',
          '!ui-px-6': '!ui-px-4',
          'ui-border-solid ui-border': 'ui-border-0',
          'ui-rounded-md':
            'ui-rounded-md ui-rounded-tr-none ui-rounded-br-none',
        }}
        icon={
          !filtersVisible && !isMobileDevice ? (
            <svg
              viewBox="0 0 16 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="ui-w-4"
            >
              <path
                d="M0.970124 0.727614C0.710128 0.522617 0.332635 0.570117 0.127638 0.830112C-0.0773581 1.09011 -0.0298589 1.4676 0.230136 1.6726L15.0299 13.2724C15.2899 13.4774 15.6674 13.4299 15.8724 13.1699C16.0774 12.9099 16.0299 12.5324 15.7699 12.3274L13.1399 10.2674C14.1299 9.25247 14.7999 8.11498 15.1374 7.3075C15.2199 7.11 15.2199 6.89001 15.1374 6.69251C14.7649 5.80002 13.9824 4.50005 12.8124 3.41507C11.6374 2.32009 10.02 1.4001 8 1.4001C6.29503 1.4001 4.87505 2.05759 3.76757 2.92008L0.970124 0.727614ZM4.74506 3.68506C5.65004 3.04257 6.73752 2.60008 8 2.60008C9.62997 2.60008 10.9699 3.34007 11.9974 4.29255C12.9599 5.18754 13.6249 6.25002 13.9649 7C13.6499 7.69999 13.0499 8.66997 12.1924 9.52246L10.8474 8.46748C11.0749 8.02749 11.2024 7.52999 11.2024 7C11.2024 5.23253 9.76997 3.80006 8.0025 3.80006C7.19751 3.80006 6.46003 4.09755 5.89754 4.58755L4.74506 3.68506ZM9.87247 7.70499L7.835 6.10752C7.94 5.89502 8 5.65253 8 5.40003C8 5.26253 7.9825 5.12754 7.95 5.00004C7.9675 5.00004 7.9825 5.00004 8 5.00004C9.10498 5.00004 9.99996 5.89502 9.99996 7C9.99996 7.2475 9.95497 7.485 9.87247 7.70499ZM10.1075 10.9624C9.46997 11.2349 8.76749 11.3999 8 11.3999C6.37003 11.3999 5.03005 10.6599 4.00257 9.70746C3.04009 8.81247 2.3751 7.74999 2.0351 7C2.2426 6.54001 2.5726 5.96252 3.02009 5.38003L2.0776 4.63755C1.50761 5.38003 1.10012 6.12002 0.862625 6.69251C0.780127 6.89001 0.780127 7.11 0.862625 7.3075C1.23512 8.19998 2.01761 9.49996 3.18758 10.5849C4.36256 11.6799 5.98004 12.5999 8 12.5999C9.19498 12.5999 10.2475 12.2774 11.1549 11.7874L10.1075 10.9624ZM4.80006 7C4.80006 8.76747 6.23253 10.1999 8 10.1999C8.33249 10.1999 8.65249 10.1499 8.95498 10.055L7.55001 8.94997C6.96252 8.81497 6.47253 8.41998 6.20753 7.89249L4.80506 6.78751C4.80006 6.85751 4.79756 6.92751 4.79756 7H4.80006Z"
                fill="black"
              />
            </svg>
          ) : (
            <svg
              viewBox="0 0 16 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="ui-w-4"
            >
              <path
                d="M1.2875 0C0.578125 0 0 0.578125 0 1.2875C0 1.6125 0.121875 1.925 0.34375 2.16563L3.25 5.29375V9.23125C3.25 9.65 3.45937 10.0437 3.8125 10.275L6.17188 11.8313C6.3375 11.9406 6.53438 12 6.73438 12C7.29688 12 7.75313 11.5437 7.75313 10.9812V5.29375L10.6594 2.16563C10.8813 1.92813 11.0031 1.61562 11.0031 1.2875C11 0.578125 10.4219 0 9.7125 0H1.2875ZM4.55 4.49062L1.775 1.5H9.22812L6.45312 4.49062C6.325 4.62812 6.25313 4.8125 6.25313 5V10.0875L4.75313 9.09688L4.75 5C4.75 4.80937 4.67813 4.62812 4.55 4.49062ZM10.75 10.25C10.3344 10.25 10 10.5844 10 11C10 11.4156 10.3344 11.75 10.75 11.75H15.25C15.6656 11.75 16 11.4156 16 11C16 10.5844 15.6656 10.25 15.25 10.25H10.75ZM10 6C10 6.41563 10.3344 6.75 10.75 6.75H15.25C15.6656 6.75 16 6.41563 16 6C16 5.58437 15.6656 5.25 15.25 5.25H10.75C10.3344 5.25 10 5.58437 10 6ZM12.75 0.25C12.3344 0.25 12 0.584375 12 1C12 1.41562 12.3344 1.75 12.75 1.75H15.25C15.6656 1.75 16 1.41562 16 1C16 0.584375 15.6656 0.25 15.25 0.25H12.75Z"
                fill="#111827"
              />
            </svg>
          )
        }
        label={t('filters')}
        labelClassName="ui-text-sm ui-font-semibold"
      />
    </div>
  );
};
